import { Stack, Tag } from '@chakra-ui/react';
import Link from 'components/Link';
import usePortalsQuery from 'hooks/usePortalsQuery';
import { get, isEqual, isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutlineSwitchAccount } from 'react-icons/md';

import { Make } from '../../api/types';
import { StatusTag } from '../../components/StatusTag';
import { DataTable } from '../../components/Table/DataTable';
import { SelectColumnFilter } from '../../components/Table/Filter';
import { MultiSelectDropDown } from '../../components/Table/MultiSelectDropDown';
import { useUserContext } from '../../context/UserContextProvider';
import { createDropdownPlainOptions, createDropdownTranslatedStaticOptions } from '../../helpers/general';
import useAccountsQuery from '../../hooks/useAccountsQuery';
import useOPUserQuery from '../../hooks/useOPUserQuery';
import { ContentCard } from '../../layout/ContentCard';

const initialFilters = {
  searchString: '',
  itemsPerPage: 20,
  status: [],
  portalId: undefined,
  type: undefined,
  country: undefined,
};

const AccountsOverviewCard = () => {
  const { t } = useTranslation();
  const { statusCodes, config } = useUserContext();

  const portalsQuery = usePortalsQuery();
  const accountsStatusCodes = get(statusCodes, 'account', {});
  const initialStatus = Object.keys(accountsStatusCodes);

  const portals = portalsQuery.data?.reduce(
    (acc, curr) => {
      acc.push({ label: curr.name, value: curr.id });
      return acc;
    },
    [] as { label: string; value: number }[],
  );

  const dropdownCountries = Object.values(config?.countries);

  const localStorageData = window.localStorage.getItem('accounts_filters');
  const destringifiedData = JSON.parse(localStorageData!) || {};
  useEffect(() => {
    if (!localStorageData) {
      window.localStorage.setItem('accounts_filters', JSON.stringify(initialFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [searchString, setSearchString] = useState(initialFilters?.searchString);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(destringifiedData?.itemsPerPage || initialFilters?.itemsPerPage);
  const [portalId, setPortalId] = useState(destringifiedData?.portalId || initialFilters?.portalId);
  const [status, setStatus] = useState(destringifiedData?.status || initialFilters?.status);
  const [country, setCountry] = useState(destringifiedData?.country || initialFilters?.country);
  const [filtersSelected, setFiltersSelected] = useState(false);
  const accountsQuery = useAccountsQuery({
    searchString,
    page,
    itemsPerPage,
    status,
    portalId,
    country,
  });

  const currentPage = accountsQuery.data?.meta?.current_page - 1;
  const initialLoad =
    searchString === '' && itemsPerPage === 20 && status === undefined && page === 1 && isEqual(initialStatus, status) && isNil(country);
  useEffect(() => {
    setPage(1);
  }, [searchString, itemsPerPage, status, portalId, country]);

  useEffect(() => {
    setFiltersSelected(status.length !== 0 || !isNil(portalId));

    window.localStorage.setItem(
      'accounts_filters',
      JSON.stringify({
        searchString: searchString,
        status: status,
        itemsPerPage: itemsPerPage,
        portalId: portalId,
        country: country,
      }),
    );
  }, [searchString, itemsPerPage, status, portalId, country]);
  const columns = useMemo(
    () => [
      {
        Header: t('account:data_table.account_details.label'),
        accessor: 'id',
        Cell: (cellProps: any) => {
          const { row } = cellProps;
          const paddedId = String(row.original.id).padStart(7, '0');
          return (
            <Link
              // @ts-ignore
              to={`/app/op/account/${row.original.id}`}
              data-test-id={'edit-' + row.index}
            >
              {`A-${paddedId}`}
            </Link>
          );
        },
      },
      {
        Header: t('account:data_table.status.label'),
        accessor: 'status',
        // @ts-ignore
        Cell: ({ value }) => <StatusTag resourceKey={'account'} currentStatus={value} />,
        shouldFilter: true,
        // @ts-ignore
        Filter: () => MultiSelectDropDown(status, setStatus, 'account', accountsStatusCodes, undefined, t('forms:status.label')),
      },
      {
        Header: t('forms:assignee.label'),
        accessor: 'assignee',
        // @ts-ignore
        Cell: (cellProps: { row: any }) => {
          const { row } = cellProps;
          const userId = row.original.assignee;
          return useOPUserQuery(userId).data?.name ?? '-';
        },
      },
      {
        Header: t('account:data_table.username.label'),
        accessor: 'username',
        Cell: (cellProps: { row: any }) => {
          const { row } = cellProps;
          return <Tag variant={'primary'}>{row?.original?.username ? row?.original?.username : '-'}</Tag>;
        },
      },
      {
        Header: t('account:data_table.portal_name.label'),
        accessor: 'portal',
        Cell: (cellProps: any) => {
          const { row } = cellProps;
          return <Tag variant={'primary'}>{row.original.portal}</Tag>;
        },
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: portalId,
            column,
            dropdownOptions: createDropdownPlainOptions({
              records: portals!,
              valueColumn: 'value',
              labelColumn: 'label',
            }),
            onChange: setPortalId,
          }),
      },
      {
        Header: t('account:data_table.makes.label'),
        accessor: 'makes',
        Cell: (cellProps: any) => {
          const { row } = cellProps;
          const allMakes = row.original.makes.reduce((makesArray: String[], make: Make) => {
            makesArray.push(make?.name);
            return makesArray;
          }, []);
          return <Tag variant={'primary'}>{allMakes.length > 0 ? allMakes.join(', ') : '-'}</Tag>;
        },
      },
      {
        Header: t('account:data_table.workshop_readable_id.label'),
        accessor: 'workshop_id',
        Cell: (cellProps: any) => {
          const { row } = cellProps;
          const paddedId = String(row.original.workshop_id).padStart(7, '0');
          return (
            <Link
              // @ts-ignore
              to={`/app/op/workshops/${row.original.workshop_id}`}
              data-test-id={'edit-' + row.index}
            >
              {`W-${paddedId}`}
            </Link>
          );
        },
      },
      {
        Header: t('account:data_table.workshop_name.label'),
        accessor: 'workshop_name',
        Cell: (cellProps: any) => {
          const { row } = cellProps;
          return <Tag variant={'primary'}>{row.original.workshop_name}</Tag>;
        },
      },
      {
        Header: t('workshop:country.label'),
        accessor: 'workshop_country',
        shouldFilter: true,
        // @ts-ignore
        Cell: (cellProps: { row: any }) => {
          const { row } = cellProps;
          const country = row.original.workshop_country;
          return t(`location:countries.${country}`);
        },
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: country,
            column,
            dropdownOptions: createDropdownTranslatedStaticOptions({
              records: dropdownCountries,
              translationKey: 'location:countries',
            }),
            onChange: setCountry,
          }),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountsStatusCodes, searchString, status, portalId, portalsQuery.data, country],
  );
  return (
    <ContentCard
      icon={<MdOutlineSwitchAccount size={40} />}
      minH="fit-content"
      header={t('account:status.accounts.label')}
      data-test-id="accounts-overview-card"
    >
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w={'100%'}>
          <DataTable
            resetFilter={() => {
              setSearchString(initialFilters?.searchString);
              setPage(1);
              setStatus(initialFilters?.status);
              setItemsPerPage(initialFilters?.itemsPerPage);
              setPortalId(initialFilters?.portalId);
            }}
            data-test-id="accounts-table"
            data={accountsQuery.data?.data || []}
            globalFilter={searchString}
            filtersSelected={filtersSelected}
            // @ts-ignore
            setGlobalFilter={setSearchString}
            autoFocus={true}
            globalFilterPlaceholder={t('account:search')}
            // @ts-ignore
            hasFilters={true}
            hasSearchInput={true}
            currentPage={currentPage}
            perPage={itemsPerPage}
            total={accountsQuery.data?.meta?.last_page || 1}
            columns={columns}
            setPage={setPage}
            setPerPage={setItemsPerPage}
            loading={accountsQuery.isLoading}
            isMoreThenOnePage={!initialLoad || Boolean(accountsQuery.data?.next_page_url) || currentPage > 0}
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};
export default AccountsOverviewCard;
